import React, { useState, useEffect } from 'react';
import { trainingApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const WorkloadDashboard = () => {
  const { user } = useAuth();
  const isManager = user?.role === 'Manager';

  const [workloadData, setWorkloadData] = useState(null);
  const [pmConfig, setPmConfig] = useState([]);
  const [workloadSettings, setWorkloadSettings] = useState(null);
  const [autoAssignOrders, setAutoAssignOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [assignmentResult, setAssignmentResult] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [selectedPM, setSelectedPM] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);

  const [editingPmId, setEditingPmId] = useState(null);
  const [rowDraft, setRowDraft] = useState({ exclusiveAccounts: '', poolParticipant: false });
  const [savingPmId, setSavingPmId] = useState(null);
  const [editingWeights, setEditingWeights] = useState(false);
  const [weightsDraft, setWeightsDraft] = useState({ jobCountWeight: 0.6, jobValueWeight: 0.4, trainingProjectWeight: 0.3 });
  const [savingWeights, setSavingWeights] = useState(false);
  const [rulesError, setRulesError] = useState(null);

  useEffect(() => {
    fetchPMConfig();
    fetchWorkloadData();
    fetchAutoAssignOrders();
    fetchWorkloadSettings();
    fetchMonthlyData();
  }, []);

  const fetchMonthlyData = async () => {
    try {
      const response = await trainingApi.getWorkloadByMonth();
      setMonthlyData(response.data);
    } catch (err) {
      console.error('Error fetching monthly workload:', err);
      setMonthlyData(null);
    }
  };

  const fetchPMConfig = async () => {
    try {
      const response = await trainingApi.getPMWorkloadConfig();
      setPmConfig(response.data);
    } catch (err) {
      console.error('Error fetching PM configuration:', err);
      setPmConfig([]);
    }
  };

  const fetchWorkloadSettings = async () => {
    try {
      const response = await trainingApi.getWorkloadSettings();
      const s = response.data || {};
      setWorkloadSettings({
        jobCountWeight: s.job_count_weight?.value ?? 0.6,
        jobValueWeight: s.job_value_weight?.value ?? 0.4,
        trainingProjectWeight: s.training_project_weight?.value ?? 0.3
      });
    } catch (err) {
      console.error('Error fetching workload settings:', err);
      setWorkloadSettings({ jobCountWeight: 0.6, jobValueWeight: 0.4, trainingProjectWeight: 0.3 });
    }
  };

  const startRowEdit = (pm) => {
    setRulesError(null);
    setEditingPmId(pm.id);
    setRowDraft({
      exclusiveAccounts: pm.exclusiveAccounts.join(', '),
      poolParticipant: pm.poolParticipant,
      includeTrainingProjects: pm.includeTrainingProjects
    });
  };

  const cancelRowEdit = () => {
    setEditingPmId(null);
    setRulesError(null);
  };

  const saveRowEdit = async (pm) => {
    setSavingPmId(pm.id);
    setRulesError(null);
    try {
      const accounts = rowDraft.exclusiveAccounts
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      await trainingApi.updatePMWorkloadConfig(pm.id, {
        exclusiveAccounts: accounts,
        poolParticipant: rowDraft.poolParticipant,
        includeTrainingProjects: rowDraft.includeTrainingProjects
      });
      await fetchPMConfig();
      setEditingPmId(null);
    } catch (err) {
      console.error('Error saving PM rule:', err);
      setRulesError(err.message || 'Failed to save');
    } finally {
      setSavingPmId(null);
    }
  };

  const startWeightsEdit = () => {
    setRulesError(null);
    setWeightsDraft({ ...workloadSettings });
    setEditingWeights(true);
  };

  const cancelWeightsEdit = () => {
    setEditingWeights(false);
    setRulesError(null);
  };

  const saveWeightsEdit = async () => {
    setSavingWeights(true);
    setRulesError(null);
    try {
      const jc = Number(weightsDraft.jobCountWeight);
      const jv = Number(weightsDraft.jobValueWeight);
      const tp = Number(weightsDraft.trainingProjectWeight);
      if (Math.abs((jc + jv) - 1) > 0.01) {
        setRulesError('Job count + job value must sum to 1.0');
        setSavingWeights(false);
        return;
      }
      await trainingApi.updateWorkloadSettings({
        jobCountWeight: jc,
        jobValueWeight: jv,
        trainingProjectWeight: tp
      });
      await fetchWorkloadSettings();
      setEditingWeights(false);
    } catch (err) {
      console.error('Error saving weights:', err);
      setRulesError(err.message || 'Failed to save weights');
    } finally {
      setSavingWeights(false);
    }
  };

  const fetchAutoAssignOrders = async () => {
    try {
      const response = await trainingApi.getOrdersForAutoAssignment();
      setAutoAssignOrders(response.data || []);
    } catch (err) {
      console.error('Error fetching orders for auto-assignment:', err);
      setAutoAssignOrders([]);
    }
  };

  const fetchWorkloadData = async () => {
    try {
      setLoading(true);
      const response = await trainingApi.getWorkload();
      setWorkloadData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching workload data:', err);
      setError(err.message || 'Failed to load workload data');
    } finally {
      setLoading(false);
    }
  };

  const calculateScore = (pmLoad, avgCount, avgValue) => {
    const normalizedCount = avgCount > 0 ? pmLoad.jobCount / avgCount : 0;
    const normalizedValue = avgValue > 0 ? pmLoad.totalValue / avgValue : 0;
    const jc = workloadSettings?.jobCountWeight ?? 0.6;
    const jv = workloadSettings?.jobValueWeight ?? 0.4;
    return (jc * normalizedCount) + (jv * normalizedValue);
  };

  const handleSelectOrder = (orderNumber) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderNumber)) {
        return prev.filter(o => o !== orderNumber);
      }
      return [...prev, orderNumber];
    });
  };

  const handleSelectAll = () => {
    if (selectedOrders.length === autoAssignOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(autoAssignOrders.map(o => o.orderNumber));
    }
  };

  const handleAutoAssign = async () => {
    if (selectedOrders.length === 0) {
      alert('Please select at least one order');
      return;
    }

    setAssigning(true);
    setAssignmentResult(null);

    try {
      const response = selectedPM
        ? await trainingApi.assignOrdersToPM(selectedOrders, selectedPM)
        : await trainingApi.autoAssignOrders(selectedOrders);
      setAssignmentResult({
        success: true,
        data: response.data,
        message: response.message
      });
      fetchAutoAssignOrders();
      fetchWorkloadData();
      fetchMonthlyData();
      setSelectedOrders([]);
      setSelectedPM(null);
    } catch (err) {
      console.error('Assignment failed:', err);
      setAssignmentResult({
        success: false,
        message: err.response?.data?.message || err.message || 'Assignment failed'
      });
    } finally {
      setAssigning(false);
    }
  };

  if (loading && !workloadData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading workload data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        Error: {error}
      </div>
    );
  }

  const poolPMs = workloadData?.filter(pm =>
    pmConfig.find(p => p.name === pm.name)?.poolParticipant
  ) || [];

  const totalOrders = poolPMs.reduce((sum, pm) => sum + pm.jobCount, 0);
  const totalProjects = poolPMs.reduce((sum, pm) => sum + pm.trainingProjects, 0);
  const totalValue = poolPMs.reduce((sum, pm) => sum + pm.totalValue, 0);
  const avgLoad = poolPMs.length > 0 ? (totalOrders + totalProjects) / poolPMs.length : 0;

  let avgCount = 0, avgValue = 0;
  if (poolPMs.length > 0) {
    avgCount = totalOrders / poolPMs.length;
    avgValue = totalValue / poolPMs.length;
  }

  // Chart data
  const chartData = {
    labels: poolPMs.map(pm => pm.name),
    datasets: [
      {
        label: 'Orders',
        data: poolPMs.map(pm => pm.jobCount),
        backgroundColor: 'rgba(59, 130, 246, 0.7)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 1
      },
      {
        label: 'Training Projects',
        data: poolPMs.map(pm => pm.trainingProjects),
        backgroundColor: 'rgba(168, 85, 247, 0.7)',
        borderColor: 'rgb(168, 85, 247)',
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      x: { stacked: true },
      y: {
        stacked: true,
        beginAtZero: true,
        title: { display: true, text: 'Count' }
      }
    },
    plugins: {
      legend: { display: true, position: 'top' }
    }
  };

  // ----- Monthly stacked bar chart (bottom of page) ----------------------
  // Three sub-series per PM: regular orders, .200 (lighter, dashed border),
  // .800 (full opacity, dotted border). All share one stack so each month's
  // bar is the total orders with a CDS date in that month.
  const pmColors = [
    { bg: 'rgba(59, 130, 246, 0.7)',  border: 'rgb(59, 130, 246)'  },
    { bg: 'rgba(239, 68, 68, 0.7)',   border: 'rgb(239, 68, 68)'   },
    { bg: 'rgba(34, 197, 94, 0.7)',   border: 'rgb(34, 197, 94)'   },
    { bg: 'rgba(168, 85, 247, 0.7)',  border: 'rgb(168, 85, 247)'  },
    { bg: 'rgba(249, 115, 22, 0.7)',  border: 'rgb(249, 115, 22)'  },
    { bg: 'rgba(236, 72, 153, 0.7)',  border: 'rgb(236, 72, 153)'  },
    { bg: 'rgba(20, 184, 166, 0.7)',  border: 'rgb(20, 184, 166)'  },
    { bg: 'rgba(245, 158, 11, 0.7)',  border: 'rgb(245, 158, 11)'  },
  ];

  const monthlyChartData = monthlyData ? {
    labels: monthlyData.months,
    datasets: monthlyData.pms.flatMap((pm, i) => {
      const c = pmColors[i % pmColors.length];
      const sets = [{
        label: pm.name,
        data: pm.months,
        backgroundColor: c.bg,
        borderColor: c.border,
        borderWidth: 1,
        stack: 'stack0'
      }];
      if (pm.months200 && pm.months200.some(v => v > 0)) {
        sets.push({
          label: `${pm.name} (.200)`,
          data: pm.months200,
          backgroundColor: c.bg.replace('0.7', '0.4'),
          borderColor: c.border,
          borderWidth: 1,
          borderDash: [5, 5],
          stack: 'stack0'
        });
      }
      if (pm.months800 && pm.months800.some(v => v > 0)) {
        sets.push({
          label: `${pm.name} (.800)`,
          data: pm.months800,
          backgroundColor: c.bg.replace('0.7', '1.0'),
          borderColor: c.border,
          borderWidth: 1,
          borderDash: [2, 2],
          stack: 'stack0'
        });
      }
      return sets;
    })
  } : null;

  const monthlyChartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      x: { stacked: true, title: { display: true, text: 'Month' } },
      y: { stacked: true, beginAtZero: true, title: { display: true, text: 'Order Count' } }
    },
    plugins: {
      legend: { display: true, position: 'top' },
      title: {
        display: true,
        text: `${new Date().getFullYear()} PM Workload by Month (Stacked)`,
        font: { size: 16 }
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">PM Workload & Assignment</h1>
        <p className="text-gray-600 mt-1 md:mt-2 text-sm md:text-base">Unified view of PM capacity across Orders & Training Projects</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <div className="text-xs md:text-sm font-medium text-gray-600">Total Orders</div>
          <div className="text-xl md:text-3xl font-bold text-blue-600 mt-1 md:mt-2">{totalOrders.toFixed(1)}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <div className="text-xs md:text-sm font-medium text-gray-600">Training Projects</div>
          <div className="text-xl md:text-3xl font-bold text-purple-600 mt-1 md:mt-2">{totalProjects}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <div className="text-xs md:text-sm font-medium text-gray-600">Total Value</div>
          <div className="text-xl md:text-3xl font-bold text-green-600 mt-1 md:mt-2">${totalValue.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 md:p-6">
          <div className="text-xs md:text-sm font-medium text-gray-600">Avg Load</div>
          <div className="text-xl md:text-3xl font-bold text-orange-600 mt-1 md:mt-2">{avgLoad.toFixed(1)}</div>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* PM Workload Table */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-b pb-3">PM Workload Distribution</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-600 text-white">
                  <th className="px-3 py-2 text-left">PM</th>
                  <th className="px-3 py-2 text-center">Orders</th>
                  <th className="px-3 py-2 text-center">Projects</th>
                  <th className="px-3 py-2 text-right">Value</th>
                  <th className="px-3 py-2 text-center">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {poolPMs.map(pm => {
                  const score = calculateScore(pm, avgCount, avgValue);
                  return (
                    <tr key={pm.name} className="hover:bg-gray-50">
                      <td className="px-3 py-3 font-medium">{pm.name}</td>
                      <td className="px-3 py-3 text-center">{pm.jobCount.toFixed(1)}</td>
                      <td className="px-3 py-3 text-center">{pm.trainingProjects}</td>
                      <td className="px-3 py-3 text-right">${pm.totalValue.toLocaleString()}</td>
                      <td className="px-3 py-3 text-center">
                        <span className="px-2 py-1 text-xs bg-gray-100 rounded">{score.toFixed(3)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-xs text-gray-500 mt-3">Lower score = next in line for assignment</p>
          </div>
        </div>

        {/* Workload Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-b pb-3">Workload Comparison</h2>
          <Bar data={chartData} options={chartOptions} />
        </div>

        {/* Assignment Rules */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-b pb-3">Assignment Rules</h2>
          {rulesError && (
            <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
              {rulesError}
            </div>
          )}
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 text-left">PM</th>
                <th className="px-4 py-2 text-left">Exclusive Accounts</th>
                <th className="px-4 py-2 text-center">Pool</th>
                {isManager && <th className="px-4 py-2 text-right w-32">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y">
              {pmConfig.map(pm => {
                const editing = editingPmId === pm.id;
                const saving = savingPmId === pm.id;
                return (
                  <tr key={pm.id}>
                    <td className="px-4 py-3 font-medium align-top">{pm.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 align-top">
                      {editing ? (
                        <input
                          type="text"
                          value={rowDraft.exclusiveAccounts}
                          onChange={(e) => setRowDraft({ ...rowDraft, exclusiveAccounts: e.target.value })}
                          placeholder="Comma-separated account names"
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          disabled={saving}
                        />
                      ) : (
                        pm.exclusiveAccounts.length > 0 ? pm.exclusiveAccounts.join(', ') : '-'
                      )}
                    </td>
                    <td className="px-4 py-3 text-center align-top">
                      {editing ? (
                        <input
                          type="checkbox"
                          checked={rowDraft.poolParticipant}
                          onChange={(e) => setRowDraft({ ...rowDraft, poolParticipant: e.target.checked })}
                          disabled={saving}
                          className="h-4 w-4"
                        />
                      ) : (
                        <span className={`px-2 py-1 text-xs rounded ${
                          pm.poolParticipant
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {pm.poolParticipant ? 'Yes' : 'No'}
                        </span>
                      )}
                    </td>
                    {isManager && (
                      <td className="px-4 py-3 text-right align-top">
                        {editing ? (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => saveRowEdit(pm)}
                              disabled={saving}
                              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                              {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                              onClick={cancelRowEdit}
                              disabled={saving}
                              className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startRowEdit(pm)}
                            disabled={editingPmId !== null || editingWeights}
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="mt-4 pt-4 border-t flex items-start justify-between gap-4">
            {editingWeights ? (
              <div className="flex-1 flex flex-wrap items-center gap-2 text-xs text-gray-700">
                <span><strong>Weight:</strong></span>
                <input
                  type="number" min="0" max="1" step="0.05"
                  value={weightsDraft.jobCountWeight}
                  onChange={(e) => setWeightsDraft({ ...weightsDraft, jobCountWeight: e.target.value })}
                  className="w-20 border border-gray-300 rounded px-2 py-1"
                  disabled={savingWeights}
                />
                <span>count +</span>
                <input
                  type="number" min="0" max="1" step="0.05"
                  value={weightsDraft.jobValueWeight}
                  onChange={(e) => setWeightsDraft({ ...weightsDraft, jobValueWeight: e.target.value })}
                  className="w-20 border border-gray-300 rounded px-2 py-1"
                  disabled={savingWeights}
                />
                <span>value &nbsp;|&nbsp; <strong>Training:</strong></span>
                <input
                  type="number" min="0" max="1" step="0.05"
                  value={weightsDraft.trainingProjectWeight}
                  onChange={(e) => setWeightsDraft({ ...weightsDraft, trainingProjectWeight: e.target.value })}
                  className="w-20 border border-gray-300 rounded px-2 py-1"
                  disabled={savingWeights}
                />
                <button
                  onClick={saveWeightsEdit}
                  disabled={savingWeights}
                  className="ml-2 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingWeights ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={cancelWeightsEdit}
                  disabled={savingWeights}
                  className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <p className="text-xs text-gray-500 flex-1">
                <strong>Weight:</strong> {workloadSettings
                  ? `${Math.round(workloadSettings.jobCountWeight * 100)}% job count, ${Math.round(workloadSettings.jobValueWeight * 100)}% job value`
                  : '60% job count, 40% job value'}<br />
                <strong>Training Projects:</strong> {workloadSettings
                  ? `Counted at ${Math.round(workloadSettings.trainingProjectWeight * 100)}% of order weight`
                  : 'Counted at 30% of order weight'}
              </p>
            )}
            {isManager && !editingWeights && (
              <button
                onClick={startWeightsEdit}
                disabled={editingPmId !== null}
                className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 self-start"
              >
                Edit weights
              </button>
            )}
          </div>
        </div>

        {/* Auto-Assignment */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-b pb-3">Auto-Assignment</h2>

          {autoAssignOrders.length === 0 ? (
            <div className="text-gray-500 text-center py-4">
              No orders available for auto-assignment
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  {autoAssignOrders.length} order(s) available
                </label>
                <button
                  onClick={handleSelectAll}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {selectedOrders.length === autoAssignOrders.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              <div className="max-h-48 overflow-y-auto border rounded-lg">
                {autoAssignOrders.map(order => (
                  <label
                    key={order.orderNumber}
                    className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedOrders.includes(order.orderNumber)}
                      onChange={() => handleSelectOrder(order.orderNumber)}
                      className="mr-3 h-4 w-4 text-blue-600 rounded"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{order.orderNumber} - {order.accountName}</div>
                      <div className="text-xs text-gray-500">
                        ${order.orderValue?.toLocaleString() || 0} | Posted: {order.postedDate}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <p className="text-xs text-gray-500">
                Orders posted last 60 days, CDS blank, not cancelled, not already assigned
              </p>

              {/* Manual override: pick a specific PM instead of letting the
                  scoring rule decide. Empty value keeps the auto-assign path. */}
              <div className="mt-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign to PM:
                </label>
                <select
                  value={selectedPM || ''}
                  onChange={(e) => setSelectedPM(e.target.value || null)}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Auto-select lowest score PM</option>
                  {poolPMs
                    .slice()
                    .sort((a, b) => calculateScore(a, avgCount, avgValue) - calculateScore(b, avgCount, avgValue))
                    .map(pm => (
                      <option key={pm.email} value={pm.email}>
                        {pm.name} (Score: {calculateScore(pm, avgCount, avgValue).toFixed(3)})
                      </option>
                    ))}
                </select>
              </div>

              <button
                onClick={handleAutoAssign}
                disabled={selectedOrders.length === 0 || assigning}
                className={`w-full px-4 py-2 rounded-lg transition font-medium ${
                  selectedOrders.length === 0 || assigning
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {assigning
                  ? 'Assigning...'
                  : selectedPM
                    ? `Assign ${selectedOrders.length} Order(s) to ${poolPMs.find(pm => pm.email === selectedPM)?.name || 'Selected PM'}`
                    : `Assign ${selectedOrders.length} Order(s) to Lowest Score PM`}
              </button>
            </div>
          )}

          {assignmentResult && (
            <div className={`mt-4 p-4 rounded-lg ${
              assignmentResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              {assignmentResult.success ? (
                <>
                  <div className="text-lg font-bold text-green-700">Assignment Successful</div>
                  <div className="text-sm text-green-600 mt-2">
                    <strong>Assigned to:</strong> {assignmentResult.data?.assignedTo?.name}<br />
                    <strong>Email:</strong> {assignmentResult.data?.assignedTo?.email}<br />
                    <strong>Composite Score:</strong> {assignmentResult.data?.assignedTo?.compositeScore?.toFixed(3)}<br />
                    <strong>Orders Assigned:</strong> {assignmentResult.data?.ordersAssigned}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-lg font-bold text-red-700">Assignment Failed</div>
                  <div className="text-sm text-red-600 mt-2">{assignmentResult.message}</div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Yearly stacked bar chart — total orders per month per PM, split
          into base/.200/.800 series so each month's bar height equals the
          total scheduled orders for that month. */}
      {monthlyChartData && (
        <div className="bg-white rounded-lg shadow p-6 mt-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 border-b pb-3">Yearly Workload by Month (Stacked)</h2>
          <Bar data={monthlyChartData} options={monthlyChartOptions} />
        </div>
      )}
    </div>
  );
};

export default WorkloadDashboard;
